import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Card, Alert, Row, Col, notification } from 'antd';
import { CameraOutlined, StopOutlined } from '@ant-design/icons';
import { BrowserMultiFormatReader } from '@zxing/library';

/*
SimpleBarcodeScanner - Single barcode scanning component
- Uses the same reliable logic as ContinuousBarcodeScanner
- Designed for single barcode capture (like in add item modal)
- Includes audio feedback and proper error handling
- Auto-closes on successful scan
*/

const SimpleBarcodeScanner = ({
  visible,
  onClose,
  onScan,
  title = "Scan Barcode"
}) => {
  // UI / scanner state
  const [isScanning, setIsScanning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [flashOk, setFlashOk] = useState(false);
  const [manualInput, setManualInput] = useState('');

  // Scan dedupe and audio
  const lastProcessedCodeRef = useRef(null);
  const lastProcessedAtRef = useRef(0);
  const nextAllowedScanAtRef = useRef(0);
  const audioCtxRef = useRef(null);

  // Video/scanner
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const manualInputRef = useRef(null);
  const manualSequenceStartRef = useRef(0);
  const manualLastChangeRef = useRef(0);
  const manualLastLengthRef = useRef(0);

  // Initialize scanner instance
  useEffect(() => {
    codeReaderRef.current = new BrowserMultiFormatReader();
    return () => {
      try { codeReaderRef.current?.reset(); } catch {}
    };
  }, []);

  // Stop scanner when modal is closed
  useEffect(() => {
    if (!visible) {
      try { codeReaderRef.current?.reset(); } catch {}
      setIsScanning(false);
      setIsLoading(false);
      setStatus('');
      setError('');
      setManualInput('');
      lastProcessedCodeRef.current = null;
      lastProcessedAtRef.current = 0;
      nextAllowedScanAtRef.current = 0;
    }
  }, [visible]);

  // Focus manual input when modal opens so HID scanners can type directly
  useEffect(() => {
    if (visible && manualInputRef.current) {
      const id = setTimeout(() => {
        manualInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(id);
    }
  }, [visible]);

  // Initialize audio context lazily
  const ensureAudio = async () => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) audioCtxRef.current = new Ctx();
      }
      if (audioCtxRef.current?.state === 'suspended') {
        await audioCtxRef.current.resume();
      }
    } catch (e) {
      // ignore
    }
  };

  const beep = async (freq = 820, ms = 90) => {
    try {
      await ensureAudio();
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.value = 0.2;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      osc.start(now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + ms / 1000);
      osc.stop(now + ms / 1000);
    } catch (e) {
      // Best-effort, don't block
    }
  };

  const startScanning = async () => {
    setError('');
    setStatus('Initializing camera...');
    setIsLoading(true);
    
    try {
      await ensureAudio(); // unlock audio on click path
      const reader = codeReaderRef.current;
      const devices = await reader.listVideoInputDevices();
      if (!devices || !devices.length) throw new Error('No camera devices found');

      let deviceId = devices[0].deviceId;
      const back = devices.find(d => d.label?.toLowerCase().includes('back'));
      if (back) deviceId = back.deviceId;

      setIsScanning(true);
      setStatus('Starting camera...');

      await reader.decodeFromVideoDevice(
        deviceId,
        videoRef.current,
        (result, err) => {
          if (!visible) return; // safety
          if (result) {
            const code = result.getText();
            const now = Date.now();
            if (now < nextAllowedScanAtRef.current) return; // global cooldown
            
            // Frame dedupe: avoid spamming the same code if camera lingers
            const lastCode = lastProcessedCodeRef.current;
            const lastTs = lastProcessedAtRef.current;
            if (code === lastCode && now - lastTs < 300) {
              return; // too soon from same frame
            }

            lastProcessedCodeRef.current = code;
            lastProcessedAtRef.current = now;
            nextAllowedScanAtRef.current = now + 250; // brief global cooldown

            // Visual and audio feedback
            beep(900, 60);
            setFlashOk(true);
            setTimeout(() => setFlashOk(false), 180);

            // Success notification
            notification.success({ 
              message: 'Barcode Scanned', 
              description: `Barcode: ${code}` 
            });

            // Call the onScan callback and close modal
            onScan?.(code);
            onClose?.();
          }
          // err can be a normal decode attempt miss; ignore
        }
      );

      setStatus('Camera ready. Point at a barcode.');
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to start camera');
      setStatus('');
      setIsScanning(false);
    } finally {
      setIsLoading(false);
    }
  };

  const stopScanning = () => {
    try { codeReaderRef.current?.reset(); } catch {}
    setIsScanning(false);
    setStatus('Scanner stopped');
  };

  const handleManualSubmit = () => {
    const value = manualInput.trim();
    if (!value) return;
    onScan?.(value);
    setManualInput('');
    manualLastLengthRef.current = 0;
    manualLastChangeRef.current = 0;
    manualSequenceStartRef.current = 0;
    onClose?.();
  };

  const handleManualChange = (e) => {
    const value = e.target.value;
    const now = Date.now();
    const prevLen = manualLastLengthRef.current || 0;
    const lastChange = manualLastChangeRef.current || 0;

    if (!lastChange || now - lastChange > 300) {
      manualSequenceStartRef.current = now;
    }

    manualLastChangeRef.current = now;
    manualLastLengthRef.current = value.length;
    setManualInput(value);

    const seqStart = manualSequenceStartRef.current || now;
    const isScannerLike = value.length >= 6 && (now - seqStart) <= 180 && value.length - prevLen >= 4;

    if (isScannerLike) {
      const trimmed = value.trim();
      if (trimmed) {
        onScan?.(trimmed);
        setManualInput('');
        manualLastLengthRef.current = 0;
        manualLastChangeRef.current = 0;
        manualSequenceStartRef.current = 0;
        onClose?.();
      }
    }
  };

  const getStatusAlert = () => {
    if (error) return <Alert message={error} type="error" showIcon />;
    if (status) return <Alert message={status} type="info" showIcon />;
    return null;
  };

  if (!visible) return null;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CameraOutlined style={{ color: 'var(--primary-color)' }} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{title}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Point your camera at a barcode to scan</div>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width="90%"
      style={{ maxWidth: 800 }}
      footer={null}
      destroyOnClose
      centered
    >
      <div>
        {getStatusAlert()}

        <Row gutter={[16, 16]}>
          {/* Scanner panel */}
          <Col xs={24}>
            <Card bodyStyle={{ padding: 0 }} style={{ borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ position: 'relative', background: '#000', height: 400 }}>
                <video
                  ref={videoRef}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: isScanning ? 'block' : 'none' }}
                  playsInline
                  muted
                />
                {!isScanning && (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#f5f7fa,#c3cfe2)' }}>
                    <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                      <CameraOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                      <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }}>Camera Ready</div>
                      <div style={{ fontSize: 12 }}>Click Start Scanning to begin</div>
                    </div>
                  </div>
                )}
                {isScanning && (
                  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                    <CornerGuides flash={flashOk} />
                    <div style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(0,0,0,0.35)', color: 'var(--text-inverse)', padding: '4px 8px', borderRadius: 6, fontSize: 12 }}>
                      Aim the barcode within the guide corners
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
              <Col xs={24}>
                {!isScanning ? (
                  <Button type="primary" size="large" icon={<CameraOutlined />} onClick={startScanning} loading={isLoading} disabled={isLoading} block>
                    {isLoading ? 'Starting Camera...' : 'Start Scanning'}
                  </Button>
                ) : (
                  <Button size="large" icon={<StopOutlined />} onClick={stopScanning} block>
                    Stop Scanning
                  </Button>
                )}
              </Col>
            </Row>

            {/* Manual / scanner text input */}
            <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
              <Col xs={24}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    ref={manualInputRef}
                    value={manualInput}
                    onChange={handleManualChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleManualSubmit();
                      }
                    }}
                    placeholder="Type or scan barcode here"
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border-color, #d9d9d9)',
                      fontSize: 14
                    }}
                  />
                  <Button
                    type="default"
                    onClick={handleManualSubmit}
                    disabled={!manualInput.trim()}
                  >
                    Use
                  </Button>
                </div>
                <div style={{ fontSize: 11, marginTop: 4, color: 'var(--text-secondary)' }}>
                  If you have a USB/Bluetooth scanner, make sure this field is focused and just scan – the code will be used automatically.
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default SimpleBarcodeScanner;

// Corner guides overlay component (inline for simplicity)
function CornerGuides({ flash }) {
  const color = flash ? 'var(--success-color)' : 'rgba(255,255,255,0.9)';
  const thick = 4;
  const size = 28;
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      {/* mask */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />
      {/* box area */}
      <div style={{ position: 'absolute', top: '18%', left: '12%', width: '76%', height: '56%' }}>
        {/* top-left corner */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: size, height: thick, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, width: thick, height: size, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        {/* top-right */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: size, height: thick, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        <div style={{ position: 'absolute', top: 0, right: 0, width: thick, height: size, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        {/* bottom-left */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: size, height: thick, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: thick, height: size, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        {/* bottom-right */}
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: size, height: thick, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: thick, height: size, background: color, borderRadius: 2, transition: 'background-color 120ms' }} />
      </div>
    </div>
  );
}
